'use client';

// Voice mode's app-level root (BUILD 2 C2-8), mounted once inside the chat
// runtime by app.tsx. It renders nothing; it connects the voice controller to
// this thread:
//   - binds the thread (append, the composer, run and cancel, the question
//     gate); the chat pane remounts per thread, so an unmount is a thread
//     switch or a sign-out, and voice mode ends there (a pending turn stays a
//     draft of its own thread);
//   - uses the company/person config loaded by SpeechProvider;
//   - hands the controller the thread's state (running, the last message's
//     visible `text` parts only: never reasoning, tool input or output, or
//     data parts), for the reply and read-aloud;
//   - the page hidden, and the keys: Esc (the pending turn, the open turn,
//     then voice mode) and Enter on a
//     pending turn (Send now).
// The required thread id keeps drafts and read-aloud sources bound to this pane.
import { useQuestionGate } from '@/billing/context';
import { useCompany } from '@/company/context';
import { createSpeechLanguageContext, speechConversationRevision } from '@/speech/conversation';
import { fetchSpeechLanguageHints } from '@/speech/transport/http';
import { ResumeCancellationContext } from '@/lib/resume-cancellation';
import { useSpeech } from '@/speech/ui/speech-provider';
import { useAuiState, useComposerRuntime, useThreadRuntime } from '@assistant-ui/react';
import { useContext, useEffect, useMemo, useRef } from 'react';
import type { VoiceBinding } from './controller';
import { voiceController, voiceStore } from './runtime';
import { useVoiceConfig, useVoiceModeActive } from './state';
import { barActive } from './store';
import { cancelBeforeSend } from './cancel-before-send';

type MessageLike = {
  id: string;
  role: string;
  content: readonly { type: string; text?: string }[];
};

/** The visible reply text of a message: its `text` parts only. */
export function visibleText(message: MessageLike | undefined): string {
  if (!message) return '';
  return message.content
    .filter((part) => part.type === 'text' && typeof part.text === 'string')
    .map((part) => part.text)
    .join('');
}

const isTyping = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  return Boolean(
    element?.isContentEditable || element?.closest?.('input, textarea, select, [role="textbox"]'),
  );
};

/** Escape and Enter keyboard shortcuts while voice mode holds the composer. */
function useVoiceKeys() {
  useEffect(() => {
    const controller = voiceController();
    const onKeyDown = (event: KeyboardEvent) => {
      const state = voiceStore.getState();
      if (!barActive(state.phase) || event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        controller.escape();
      } else if (event.key === 'Enter' && state.pending && !isTyping(event.target)) {
        const onButton = (event.target as HTMLElement | null)?.closest?.('button');
        if (onButton) return;
        event.preventDefault();
        controller.sendNow();
      }
    };
    globalThis.addEventListener?.('keydown', onKeyDown);
    return () => {
      globalThis.removeEventListener?.('keydown', onKeyDown);
    };
  }, []);
}

export function VoiceRoot({ threadId }: { threadId: string | null }) {
  const thread = useThreadRuntime();
  const composer = useComposerRuntime();
  const cancelResume = useContext(ResumeCancellationContext);
  const gate = useQuestionGate();
  const company = useCompany();
  const speech = useSpeech();
  const userId = company.data?.membership?.userId ?? null;
  const latest = useRef({ gate, cancelResume });
  latest.current = { gate, cancelResume };
  const languageContext = useMemo(
    () => createSpeechLanguageContext(fetchSpeechLanguageHints),
    [userId, threadId],
  );
  const active = useVoiceModeActive();
  const spokenLanguage = useVoiceConfig().voice?.prefs.spokenLanguage;
  const conversationRevision = useAuiState((state) =>
    speechConversationRevision(state.thread.messages),
  );
  const turnRunning = useAuiState((state) => state.thread.isRunning);
  useEffect(() => {
    if (active && !turnRunning && userId && threadId) void languageContext.refresh(threadId);
  }, [
    active,
    userId,
    threadId,
    conversationRevision,
    spokenLanguage,
    turnRunning,
    languageContext,
  ]);
  useEffect(() => () => languageContext.clear(), [languageContext]);

  useEffect(() => {
    const binding: VoiceBinding = {
      threadId,
      userId,
      append: (message) => thread.append(message),
      composer: {
        getState: () => ({ text: composer.getState().text }),
        setText: (text) => composer.setText(text),
        attachments: () => composer.getState().attachments.length,
      },
      running: () => thread.getState().isRunning,
      cancelRun: () => cancelBeforeSend(thread, latest.current.cancelResume),
      latestAssistantMessageId: () =>
        [...thread.getState().messages].reverse().find((message) => message.role === 'assistant')
          ?.id ?? null,
      speechLanguages: languageContext.current,
      gateAllows: () => latest.current.gate.allow(),
      focusComposer: () =>
        globalThis.requestAnimationFrame?.(() =>
          globalThis.document
            ?.querySelector<HTMLTextAreaElement>('textarea.aui-composer-input')
            ?.focus({ preventScroll: true }),
        ),
    };
    return voiceController().bind(binding);
  }, [thread, composer, threadId, userId, languageContext]);

  const running = useAuiState((state) => state.thread.isRunning);
  const lastId = useAuiState((state) => state.thread.messages.at(-1)?.id ?? null);
  const lastRole = useAuiState((state) => state.thread.messages.at(-1)?.role ?? null);
  const lastText = useAuiState((state) =>
    visibleText(state.thread.messages.at(-1) as MessageLike | undefined),
  );
  const lastParts = useAuiState((state) => state.thread.messages.at(-1)?.content.length ?? 0);
  useEffect(() => {
    voiceController().onThread({
      running,
      last:
        lastId && lastRole
          ? { id: lastId, role: lastRole, text: lastText, parts: lastParts }
          : null,
    });
  }, [running, lastId, lastRole, lastText, lastParts]);

  useEffect(() => {
    const report = () =>
      voiceController().onVisibility(globalThis.document?.visibilityState !== 'hidden');
    const hide = () => voiceController().onVisibility(false);
    globalThis.document?.addEventListener('visibilitychange', report);
    globalThis.addEventListener?.('pagehide', hide);
    return () => {
      globalThis.document?.removeEventListener('visibilitychange', report);
      globalThis.removeEventListener?.('pagehide', hide);
    };
  }, []);

  useVoiceKeys();
  return null;
}
